import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Formik, Form } from "formik";
import { Loader2 } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { isProductEnabled } from "@/business/product-access";
import { useBusiness } from "@/core/business";
import { isPayrollBusiness } from "@/core/business/payroll-only";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useIsMobile } from "@/core/component-hooks/use-mobile";
import { getInitials } from "@/core/utils/name";
import { cn } from "@/core/utils/utils";
import { PAY_BREAKDOWN_PATH } from "@/payroll/pay-breakdown/routes";
import { ROLE_TO_LABEL } from "@/user/constants";
import { useEditMyUser, useGetMyUser } from "@/user/query";
import { isApOnly } from "@/user/utils";

const profileSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  role: Yup.string().required("Role is required"),
});

export default function Profile() {
  const { data: userInfo, isLoading } = useGetMyUser();
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isPayrollOnlyBusiness = isPayrollBusiness(selectedBusiness);
  const { settings, isLoading: isProductsLoading } = useBusinessProducts();
  const apOnly = isApOnly(userInfo?.role);

  const { mutate: editUser, isPending } = useEditMyUser();

  const initialValues = useMemo(
    () => ({
      email: userInfo?.email || "",
      first_name: userInfo?.first_name || "",
      last_name: userInfo?.last_name || "",
      role: userInfo?.role || "",
    }),
    [userInfo],
  );

  const handleSubmit = useCallback(
    (values: typeof initialValues) => {
      editUser(
        {
          first_name: values.first_name,
          last_name: values.last_name,
        },
        {
          onSuccess: () => {
            toast.success("Profile saved");
          },
          onError: (error) => {
            toast.error(
              error instanceof Error
                ? error.message
                : "Profile save failed, try again",
            );
          },
        },
      );
    },
    [editUser, toast],
  );

  return (
    <Stack
      direction="row"
      align="start"
      justify="center"
      fullWidth
      className={cn("min-h-[calc(100vh-67px)] overflow-hidden pt-6", {
        "overflow-y-auto p-4": isMobile,
      })}
    >
      <Stack fullWidth className="w-full max-w-xl" gap="sm">
        {apOnly &&
          !isProductsLoading &&
          !isProductEnabled(settings, "accounts_payable") && (
            <Typography variant="bodyW2" textColor="secondary">
              Accounts Payable is not enabled for this business. Contact your
              administrator for access.
            </Typography>
          )}
        {isPayrollOnlyBusiness && !apOnly && (
          <button
            type="button"
            onClick={() => navigate(PAY_BREAKDOWN_PATH)}
            className="flex w-fit items-center gap-1 text-sm text-primary underline hover:text-primary/80"
          >
            <ArrowLeftIcon className="h-3 w-3" />
            Back to Payroll
          </button>
        )}
        <Card
          className={cn("w-full p-6 pt-8", {
            "p-4 pt-5": isMobile,
          })}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={profileSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, getFieldProps, isValid, dirty }) => (
              <Form>
                <Stack
                  className={cn("gap-y-8", {
                    "gap-y-6": isMobile,
                  })}
                  fullWidth
                >
                  <Typography
                    variant="subtitleW1"
                    className={cn("mt-4", {
                      "mt-1": isMobile,
                    })}
                  >
                    My Profile
                  </Typography>

                  <Stack
                    align={isMobile ? "center" : "start"}
                    direction={isMobile ? "column" : "row"}
                    gap={isMobile ? "md" : "xl"}
                    justify="between"
                    fullWidth
                  >
                    <Stack
                      align="center"
                      justify="center"
                      gap="none"
                      className={cn("h-24 w-24 rounded-full bg-gray-200", {
                        "h-20 w-20": isMobile,
                      })}
                    >
                      <Typography variant="initials" className="text-gray-500">
                        {getInitials(userInfo)}
                      </Typography>
                    </Stack>
                    <Stack
                      gap="lg"
                      className={cn("flex-1 pb-4", {
                        "w-full": isMobile,
                      })}
                    >
                      <Stack
                        align="start"
                        direction={isMobile ? "column" : "row"}
                        justify="between"
                        gap={isMobile ? "md" : "lg"}
                        fullWidth
                      >
                        <FormInput
                          id="first_name"
                          type="text"
                          label="First Name"
                          error={
                            touched.first_name ? errors.first_name : undefined
                          }
                          {...getFieldProps("first_name")}
                          required
                          disableRequirementAsterisk
                          fullWidth
                        />
                        <FormInput
                          id="last_name"
                          type="text"
                          label="Last Name"
                          error={
                            touched.last_name ? errors.last_name : undefined
                          }
                          {...getFieldProps("last_name")}
                          required
                          disableRequirementAsterisk
                          fullWidth
                        />
                      </Stack>

                      <FormInput
                        id="email"
                        type="email"
                        label="Email"
                        placeholder="Enter your email"
                        error={touched.email ? errors.email : undefined}
                        {...getFieldProps("email")}
                        required
                        disableRequirementAsterisk
                        fullWidth
                        disabled
                        disabledTooltip="Contact support to change email"
                      />

                      <FormInput
                        id="role"
                        disabled
                        type="text"
                        label="Role"
                        placeholder="Enter your role"
                        error={touched.role ? errors.role : undefined}
                        {...getFieldProps("role")}
                        fullWidth
                        value={
                          ROLE_TO_LABEL[
                            values.role as keyof typeof ROLE_TO_LABEL
                          ]
                        }
                        required
                        disableRequirementAsterisk
                        disabledTooltip={
                          <>
                            Have an owner account change your role from the{" "}
                            <span className="font-semibold">
                              Admin settings
                            </span>
                          </>
                        }
                      />
                    </Stack>
                  </Stack>

                  <Button
                    type="submit"
                    size="sm"
                    fullWidth={isMobile}
                    disabled={!isValid || !dirty}
                    leftIcon={
                      (isPending || isLoading) && (
                        <Loader2 className="animate-spin" />
                      )
                    }
                  >
                    Save changes
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Card>
      </Stack>
    </Stack>
  );
}
